'use client'

import { ClubPointsBlock } from 'cms-types'
import { useEffect, useMemo } from 'react'
import { ClubPoints as ClubPointsUI, type LoadableComponentProps } from 'ui'

import { getCurrentAndNextTier, resolveRelation } from '@/cms/helpers'
import { ClubMembershipStatus, useClubMembership } from '@/hooks/useClubMembership'
import { useCustomer } from '@/store/customerStore'
import { useGlobalTypography } from '@/store/globalTypographyStore'
import { useMetaObjects } from '@/store/metaObjectStore'

function renderRemainingTemplate(template: string, remainingPoints: number) {
  return template.replace('{{points}}', String(remainingPoints))
}

export function ClubPointsClient({ data, loading }: LoadableComponentProps<ClubPointsBlock>) {
  const customer = useCustomer()
  const membership = useClubMembership()
  const tierDefinitions = useMetaObjects(s => s.tierDefinitions)
  const getTierDefinitions = useMetaObjects(s => s.getTierDefinitions)
  const loadingMetaObjects = useMetaObjects(s => s.isLoading)
  const { clubTiers } = useGlobalTypography()

  useEffect(() => {
    getTierDefinitions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const points = customer.customer?.points ?? 0

  const { currentTier, nextTier } = useMemo(
    () => getCurrentAndNextTier(tierDefinitions, points),
    [tierDefinitions, points],
  )

  const remainingPoints = nextTier ? Math.max(nextTier.minimumPoints - points, 0) : 0

  // Dashboard is members-only; empty while membership resolves or for non-members
  // (ClubJoin fills the slot), so its skeleton never flashes at them.
  if (membership !== ClubMembershipStatus.Member) return null
  if (loading || loadingMetaObjects) return <ClubPointsUI loading />

  const currentTierName = currentTier
    ? (clubTiers?.[currentTier.handle as keyof typeof clubTiers] ?? currentTier.name)
    : ''

  return (
    <ClubPointsUI
      data={{
        ...data,
        points: points ?? 0,
        level: currentTierName,
        nextTierLabel: nextTier
          ? renderRemainingTemplate(data?.nextTierLabel || '', remainingPoints)
          : undefined,
        image: resolveRelation(data.image),
      }}
    />
  )
}
